import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Share2, Trash2, CheckCircle, ExternalLink, ShoppingBag, ArrowRight } from 'lucide-react';
import { selectionService, Selection } from '../../services/selection';
import { Product } from '../../services/products';
import { Button } from '../../components/common/Button';

// Helper to format price
const formatPrice = (price: string | number) => {
  return Number(price).toLocaleString('fa-IR');
};

const SelectionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeSelection, setActiveSelection] = useState<Selection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    fetchActiveSelection();
  }, []);

  const fetchActiveSelection = async () => {
    setIsLoading(true);
    try {
      const response = await selectionService.getActive();
      setActiveSelection(response.data.data);
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        setActiveSelection(null);
      } else {
        console.error('Error fetching selection:', error);
        toast.error('خطا در دریافت لیست انتخاب‌ها');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveItem = async (productId: string) => {
    try {
      await selectionService.toggleLike(productId);
      fetchActiveSelection(); // Refresh list
      toast.success('محصول از لیست حذف شد');
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('خطا در حذف محصول');
    }
  };

  const handleSubmit = async () => {
    if (!activeSelection || !activeSelection.items || activeSelection.items.length === 0) return;

    setIsSubmitting(true);
    try {
      await selectionService.submit();
      toast.success('لیست با موفقیت ثبت شد');
      navigate('/orders', { state: { tab: 'selections' } });
    } catch (error) {
      console.error('Error submitting selection:', error);
      toast.error('خطا در ثبت لیست');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShare = async () => {
    if (!activeSelection) return;

    setIsSharing(true);
    try {
      const response = await selectionService.generateShareImage(activeSelection.id);
      const imageUrl = response.image_url;

      if (navigator.share) {
        try {
            const imageBlob = await fetch(imageUrl).then(r => r.blob());
            const file = new File([imageBlob], 'selection.png', { type: 'image/png' });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: 'لیست منتخب من',
                    text: 'لیست محصولات انتخابی من در راز گلد',
                });
            } else {
                 await navigator.share({
                    title: 'لیست منتخب من',
                    text: 'لیست محصولات انتخابی من در راز گلد',
                    url: imageUrl
                });
            }
        } catch (e) {
             // Fallback if file sharing fails or user cancels
             console.log("Share failed or cancelled", e);
        }
      } else {
        window.open(imageUrl, '_blank');
        toast.success('تصویر در تب جدید باز شد');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('خطا در اشتراک‌گذاری');
    } finally {
      setIsSharing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="h-24 bg-gray-100 rounded-2xl w-full"></div>
        
        {/* Mobile List Skeleton */}
        <div className="md:hidden space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-4 rounded-xl border border-gray-100 flex gap-4">
              <div className="w-24 h-24 bg-gray-200 rounded-lg"></div>
              <div className="flex-1 space-y-3 py-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="flex justify-between items-end mt-auto">
                   <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                   <div className="h-8 bg-gray-200 rounded w-8"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table Skeleton */}
        <div className="hidden md:block bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex gap-4">
             {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-4 bg-gray-200 rounded w-1/6"></div>)}
          </div>
          {[1, 2, 3].map((i) => (
             <div key={i} className="p-4 flex gap-4 border-b border-gray-50 last:border-0">
                <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 space-y-2 py-2">
                   <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                   <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
             </div>
          ))}
        </div>

        {/* Footer Skeleton */}
        <div className="h-24 bg-white rounded-2xl border border-gray-100"></div>
      </div>
    );
  }

  if (!activeSelection || !activeSelection.items || activeSelection.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100 animate-fade-in">
        <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mb-6">
            <ShoppingBag className="w-10 h-10 text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">لیست انتخاب‌های شما خالی است</h2>
        <p className="text-gray-500 mb-8 max-w-md">
            شما هنوز هیچ محصولی را به لیست خود اضافه نکرده‌اید. برای شروع، به بخش محصولات بروید و موارد مورد علاقه خود را انتخاب کنید.
        </p>
        <Button 
            onClick={() => window.location.href = '/products'}
            variant="primary"
            className="flex items-center gap-2 px-6 py-3 rounded-xl transition-all"
        >
          مشاهده محصولات
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  const totalWeight = activeSelection.items.reduce((sum, item) => sum + (item.product?.gross_weight || 0), 0);
  const totalPrice = activeSelection.items.reduce((sum, item) => {
    return sum + Number(item.product?.calculated_price?.total_price || 0);
  }, 0);

  const isSubmitted = activeSelection.status === 'submitted';

  return (
    <div className="space-y-6 pb-32 md:pb-0 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <ShoppingBag className="w-6 h-6 text-amber-600" />
                لیست انتخاب‌های من
            </h1>
            <p className="text-gray-500 text-sm mt-1">
                {activeSelection.items.length} محصول انتخاب شده
            </p>
        </div>
        
        <div className="hidden md:flex gap-3 w-full md:w-auto">
            {isSubmitted ? (
                 <Button 
                 onClick={handleShare} 
                 isLoading={isSharing}
                 className="flex-1 md:flex-none items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
               >
                 <Share2 className="w-4 h-4" />
                 اشتراک‌گذاری لیست
               </Button>
            ) : (
                <Button 
                onClick={handleSubmit} 
                isLoading={isSubmitting}
                variant="primary"
                className="flex-1 md:flex-none items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                اتمام انتخاب و ثبت نهایی
              </Button>
            )}
        </div>
      </div>

      {/* Mobile View (Cards) */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {activeSelection.items.map((item) => {
            const product = item.product as Product;
            if (!product) return null;

            return (
                <div key={item.product_id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex gap-4 transition-transform active:scale-98">
                    <div className="w-24 h-24 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                        <img
                          src={product.image_urls?.[0] || '/assets/images/placeholder.png'}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                        <div>
                            <h3 className="font-bold text-gray-800 truncate">{product.name}</h3>
                            <p className="text-xs text-gray-500 font-mono mt-1">{product.sku}</p>
                        </div>
                        
                        <div className="flex justify-between items-end mt-2">
                            <div>
                                <div className="text-xs text-gray-500">وزن: {product.gross_weight} گرم</div>
                                <div className="text-amber-600 font-bold text-sm">
                                    {product.calculated_price ? formatPrice(product.calculated_price.total_price) : 'تماس بگیرید'} تومان
                                </div>
                            </div>
                            
                            {!isSubmitted && (
                                <button
                                    onClick={() => handleRemoveItem(product.id)}
                                    className="p-2 text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                             {isSubmitted && (
                                <a href={`/products/${product.sku}`} target="_blank" rel="noreferrer" className="p-2 text-blue-500 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                                    <ExternalLink className="w-4 h-4" />
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            );
        })}
      </div>

      {/* Desktop View (Table) */}
      <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-gray-50 text-gray-700 font-medium border-b border-gray-100">
              <tr>
                <th className="p-4 w-24">تصویر</th>
                <th className="p-4">مشخصات محصول</th>
                <th className="p-4">وزن (گرم)</th>
                <th className="p-4">قیمت (تومان)</th>
                <th className="p-4 text-center w-24">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activeSelection.items.map((item) => {
                const product = item.product as Product;
                if (!product) return null;

                return (
                  <tr key={item.product_id} className="hover:bg-gray-50 transition-colors group">
                    <td className="p-3">
                      <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={product.image_urls?.[0] || '/assets/images/placeholder.png'}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    </td>
                    <td className="p-3">
                        <div className="font-bold text-gray-800 text-base">{product.name}</div>
                        <div className="text-gray-400 font-mono text-xs mt-1">کد: {product.sku}</div>
                    </td>
                    <td className="p-3 text-gray-700 font-medium">{product.gross_weight}</td>
                    <td className="p-3 text-amber-600 font-bold text-base">
                        {product.calculated_price ? formatPrice(product.calculated_price.total_price) : 'تماس بگیرید'}
                    </td>
                    <td className="p-3 text-center">
                        {!isSubmitted && (
                             <button
                             onClick={() => handleRemoveItem(product.id)}
                             className="text-gray-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-all"
                             title="حذف از لیست"
                           >
                             <Trash2 className="w-5 h-5" />
                           </button>
                        )}
                        {isSubmitted && (
                            <a href={`/products/${product.sku}`} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-600 p-2 block">
                                <ExternalLink className="w-5 h-5 mx-auto" />
                            </a>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Footer */}
      <div className="fixed bottom-0 left-0 right-0 md:static bg-white p-4 md:p-6 md:rounded-2xl shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] md:shadow-sm border-t md:border border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 z-50">
        <div className="flex flex-row items-center justify-between w-full md:w-auto md:gap-4">
             <div className="flex items-center gap-2 text-gray-500">
                <span className="text-sm">وزن:</span>
                <span className="text-lg font-bold text-gray-800">{totalWeight.toFixed(2)} <span className="text-xs font-normal">گرم</span></span>
            </div>
            
            <div className="hidden md:block h-10 w-px bg-gray-200"></div>
            
            <div className="flex items-center gap-2 text-gray-500">
                <span className="text-sm">قیمت:</span>
                <span className="text-xl font-bold text-amber-600">{formatPrice(totalPrice)} <span className="text-xs font-normal">تومان</span></span>
            </div>
        </div>

        <div className="w-full md:w-auto">
            {isSubmitted ? (
                 <Button 
                 onClick={handleShare} 
                 isLoading={isSharing}
                 className="w-full md:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 md:py-2"
               >
                 <Share2 className="w-4 h-4" />
                 اشتراک‌گذاری لیست
               </Button>
            ) : (
                <Button 
                onClick={handleSubmit} 
                isLoading={isSubmitting}
                variant="primary"
                className="w-full md:w-auto flex items-center justify-center gap-2 py-3 md:py-2"
              >
                <CheckCircle className="w-4 h-4" />
                اتمام انتخاب و ثبت نهایی
              </Button>
            )}
        </div>
      </div>
    </div>
  );
};

export default SelectionsPage;
